import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Save, Mountain, Layers } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminFinishings() {
  const [finishings, setFinishings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price_granit: '',
    price_ceramique: '',
    description: ''
  });

  useEffect(() => {
    fetchFinishings();
  }, []);

  const fetchFinishings = async () => {
    try {
      const response = await axios.get(`${API_URL}/finishings`);
      setFinishings(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name,
        price_granit: item.price_granit ?? item.price ?? 0,
        price_ceramique: item.price_ceramique ?? 0,
        description: item.description || ''
      });
    } else {
      setEditingItem(null);
      setFormData({ name: '', price_granit: '', price_ceramique: '', description: '' });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        price_granit: parseFloat(formData.price_granit) || 0,
        price_ceramique: parseFloat(formData.price_ceramique) || 0,
        // Legacy fallback = granit
        price: parseFloat(formData.price_granit) || 0
      };
      if (editingItem) {
        await axios.put(`${API_URL}/admin/finishings/${editingItem.id}`, payload);
        toast.success('Façonnage mis à jour');
      } else {
        await axios.post(`${API_URL}/admin/finishings`, payload);
        toast.success('Façonnage créé');
      }
      setIsDialogOpen(false);
      fetchFinishings();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce façonnage ?')) return;
    try {
      await axios.delete(`${API_URL}/admin/finishings/${id}`);
      toast.success('Façonnage supprimé');
      fetchFinishings();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const renderCard = (item, index) => (
    <motion.div
      key={item.id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className="card-dashboard"
      data-testid={`finishing-card-${item.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold truncate">{item.name}</h3>
          {item.description && (
            <p className="text-white/40 text-xs mt-1 line-clamp-2">{item.description}</p>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          <button
            onClick={() => openDialog(item)}
            className="p-1.5 text-white/40 hover:text-white transition"
            data-testid={`edit-finishing-${item.id}`}
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(item.id)}
            className="p-1.5 text-white/40 hover:text-red-400 transition"
            data-testid={`delete-finishing-${item.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="bg-[#3D2818]/60 border border-[#8B5A2B]/40 p-3 rounded-sm">
          <div className="flex items-center gap-1.5 mb-1">
            <Mountain className="w-3 h-3 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-[10px] uppercase tracking-wider font-medium">Granit / Quartz</span>
          </div>
          <p className="text-white font-bold text-lg leading-tight">
            {(item.price_granit ?? item.price ?? 0).toFixed(2)}
            <span className="text-white/60 text-sm font-normal"> €</span>
          </p>
        </div>
        <div className="bg-[#1B2A3D]/60 border border-[#3B6FA1]/40 p-3 rounded-sm">
          <div className="flex items-center gap-1.5 mb-1">
            <Layers className="w-3 h-3 text-[#6BB6FF]" />
            <span className="text-[#6BB6FF] text-[10px] uppercase tracking-wider font-medium">Céramique</span>
          </div>
          <p className="text-white font-bold text-lg leading-tight">
            {(item.price_ceramique ?? 0).toFixed(2)}
            <span className="text-white/60 text-sm font-normal"> €</span>
          </p>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-6" data-testid="admin-finishings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Gestion des façonnages</h1>
          <p className="text-white/50 mt-1">{finishings.length} option(s) — 2 tarifs par façonnage (Granit vs Céramique)</p>
        </div>
        <button onClick={() => openDialog()} className="btn-primary flex items-center gap-2" data-testid="add-finishing-btn">
          <Plus className="w-4 h-4" />
          Ajouter
        </button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-white/60 pb-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm bg-[#3D2818] border border-[#8B5A2B]" />
          <span>Granit / Quartz / Silestone / Compac</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm bg-[#1B2A3D] border border-[#3B6FA1]" />
          <span>Céramique (Dekton · Laminam · Neolith · Xtone · Techlam · Uniceramica)</span>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {finishings.map(renderCard)}
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-[#1C1C1C] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Modifier le façonnage' : 'Nouveau façonnage'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-white/60 text-sm mb-2 block">Nom *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="bg-[#0A0A0A] border-white/10 text-white rounded-none"
                data-testid="finishing-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#3D2818]/40 border border-[#8B5A2B]/40 p-3 rounded-sm">
                <label className="text-[#D4AF37] text-xs uppercase tracking-wider font-medium mb-2 flex items-center gap-1.5">
                  <Mountain className="w-3 h-3" />
                  Prix Granit / Quartz (€)
                </label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price_granit}
                  onChange={(e) => setFormData({ ...formData, price_granit: e.target.value })}
                  required
                  className="bg-[#0A0A0A] border-white/10 text-white rounded-none"
                  data-testid="finishing-price-granit-input"
                />
              </div>
              <div className="bg-[#1B2A3D]/40 border border-[#3B6FA1]/40 p-3 rounded-sm">
                <label className="text-[#6BB6FF] text-xs uppercase tracking-wider font-medium mb-2 flex items-center gap-1.5">
                  <Layers className="w-3 h-3" />
                  Prix Céramique (€)
                </label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price_ceramique}
                  onChange={(e) => setFormData({ ...formData, price_ceramique: e.target.value })}
                  className="bg-[#0A0A0A] border-white/10 text-white rounded-none"
                  data-testid="finishing-price-ceramique-input"
                />
              </div>
            </div>

            <div>
              <label className="text-white/60 text-sm mb-2 block">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-[#0A0A0A] border-white/10 text-white rounded-none resize-none"
                rows={3}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button type="button" onClick={() => setIsDialogOpen(false)} className="btn-secondary flex-1">
                Annuler
              </button>
              <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="save-finishing-btn">
                <Save className="w-4 h-4" />
                Enregistrer
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
